/**
 * Validation Utilities
 * 
 * Common validation schemas and utilities using Zod for input validation
 * across the entire application to prevent injection attacks and ensure data integrity.
 */

import { z } from "zod";

/**
 * Common string validation schemas
 */
export const StringValidation = {
  // Basic non-empty string
  nonEmpty: z.string().min(1, "Value cannot be empty"),
  
  // Email validation
  email: z.string().email("Invalid email address"),
  
  // URL validation
  url: z.string().url("Invalid URL"),
  
  // UUID validation
  uuid: z.string().uuid("Invalid UUID"),
  
  // Alphanumeric with underscores (for usernames, IDs, etc.)
  alphanumeric: z.string().regex(/^[a-zA-Z0-9_]+$/, "Must be alphanumeric with underscores only"),
  
  // Alphanumeric with hyphens (for slugs, etc.)
  slug: z.string().regex(/^[a-z0-9-]+$/, "Must be lowercase alphanumeric with hyphens only"),
  
  // Safe string (no special characters that could cause injection)
  safe: z.string().regex(/^[a-zA-Z0-9\s._-]+$/, "Contains invalid characters"),
  
  // Hex color code
  hexColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Invalid hex color code"),
  
  // Phone number (international format)
  phone: z.string().regex(/^\+?[1-9]\d{1,14}$/, "Invalid phone number"),
  
  // ISO 8601 date string
  isoDate: z.string().datetime("Invalid ISO 8601 date"),
  
  // MongoDB ObjectId
  objectId: z.string().regex(/^[0-9a-fA-F]{24}$/, "Invalid ObjectId"),
  
  // Firebase document path
  firebasePath: z.string().regex(/^[a-zA-Z0-9_/.-]+$/, "Invalid Firebase path"),
  
  // HTML-safe string (strips potentially dangerous characters)
  htmlSafe: z.string().transform((val: string) => 
    val.replace(/[<>'"]/g, (char: string) => {
      const entities: Record<string, string> = {
        '<': '&lt;',
        '>': '&gt;',
        "'": '&#39;',
        '"': '&quot;'
      };
      return entities[char] || char;
    })
  ),
};

/**
 * Common number validation schemas
 */
export const NumberValidation = {
  // Positive integer
  positiveInt: z.number().int().positive("Must be a positive integer"),
  
  // Non-negative integer (including 0)
  nonNegativeInt: z.number().int().nonnegative("Must be a non-negative integer"),
  
  // Positive number
  positive: z.number().positive("Must be a positive number"),
  
  // Non-negative number (including 0)
  nonNegative: z.number().nonnegative("Must be a non-negative number"),
  
  // Percentage (0-100)
  percentage: z.number().min(0).max(100, "Must be between 0 and 100"),
  
  // Port number
  port: z.number().int().min(1).max(65535, "Invalid port number"),
  
  // Timestamp (milliseconds since epoch)
  timestamp: z.number().int().positive(),
};

/**
 * Common object validation schemas
 */
export const ObjectValidation = {
  // Pagination parameters
  pagination: z.object({
    page: z.number().int().positive().optional().default(1),
    limit: z.number().int().positive().max(100).optional().default(20),
    offset: z.number().int().nonnegative().optional(),
  }),
  
  // Sort parameters
  sort: z.object({
    field: z.string().min(1),
    order: z.enum(["asc", "desc"]).optional().default("asc"),
  }),
  
  // Date range
  dateRange: z.object({
    startDate: z.string().datetime(),
    endDate: z.string().datetime(),
  }).refine((data: { startDate: string; endDate: string }) => new Date(data.startDate) <= new Date(data.endDate), {
    message: "Start date must be before or equal to end date",
  }),
  
  // Coordinate
  coordinate: z.object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
  }),
};

/**
 * API request validation schemas
 */
export const ApiValidation = {
  // Generic API request with common headers
  headers: z.object({
    "content-type": z.string().optional(),
    "authorization": z.string().optional(),
    "user-agent": z.string().optional(),
    "x-api-key": z.string().optional(),
    "x-request-id": z.string().uuid().optional(),
  }).passthrough(), // Allow additional headers
  
  // Query parameters (string record)
  queryParams: z.record(z.string(), z.string()),
  
  // Path parameters
  pathParams: z.record(z.string(), z.string()),
};

/**
 * Sanitization utilities
 */
export const Sanitization = {
  /**
   * Sanitize HTML to prevent XSS attacks
   */
  sanitizeHtml: (input: string): string => {
    return input
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')
      .replace(/\//g, '&#x2F;');
  },
  
  /**
   * Sanitize NoSQL query strings
   * 
   * WARNING: This is NOT for SQL databases - use parameterized queries instead.
   * For Firestore/NoSQL, use this to remove potentially dangerous operators.
   * 
   * Best practice: Always use Firestore's built-in security rules and
   * structured query API instead of string-based queries.
   */
  sanitizeNoSqlQuery: (input: string): string => {
    // Remove NoSQL injection operators
    return input
      .replace(/\$where/gi, '')
      .replace(/\$ne/gi, '')
      .replace(/\$gt/gi, '')
      .replace(/\$lt/gi, '')
      .replace(/\$regex/gi, '')
      .replace(/\.\$/g, '')
      .replace(/\[.*\]/g, '');
  },
  
  /**
   * Sanitize file paths to prevent directory traversal
   */
  sanitizeFilePath: (input: string): string => {
    return input
      .replace(/\.\./g, '') // Remove parent directory references
      .replace(/\/\//g, '/') // Remove double slashes
      .replace(/^\//, '') // Remove leading slash
      .replace(/\\/g, '/'); // Normalize backslashes
  },
  
  /**
   * Sanitize shell command arguments
   */
  sanitizeShellArg: (input: string): string => {
    // Remove shell metacharacters
    return input.replace(/[;&|`$(){}[\]<>!*?~]/g, '');
  },
  
  /**
   * Trim and normalize whitespace
   */
  normalizeWhitespace: (input: string): string => {
    return input.trim().replace(/\s+/g, ' ');
  },
};

/**
 * Validation helper functions
 */
export const ValidationHelpers = {
  /**
   * Validate and sanitize email
   */
  validateEmail: (email: string): { valid: boolean; sanitized?: string; error?: string } => {
    try {
      const sanitized = email.toLowerCase().trim();
      const result = StringValidation.email.safeParse(sanitized);
      if (result.success) {
        return { valid: true, sanitized: result.data };
      }
      return { valid: false, error: result.error.errors[0]?.message };
    } catch {
      return { valid: false, error: "Invalid email format" };
    }
  },
  
  /**
   * Validate and sanitize URL
   */
  validateUrl: (url: string): { valid: boolean; sanitized?: string; error?: string } => {
    try {
      const sanitized = url.trim();
      const result = StringValidation.url.safeParse(sanitized);
      if (result.success) {
        return { valid: true, sanitized: result.data };
      }
      return { valid: false, error: result.error.errors[0]?.message };
    } catch {
      return { valid: false, error: "Invalid URL format" };
    }
  },
  
  /**
   * Validate UUID
   */
  validateUuid: (uuid: string): { valid: boolean; error?: string } => {
    try {
      const result = StringValidation.uuid.safeParse(uuid);
      if (result.success) {
        return { valid: true };
      }
      return { valid: false, error: result.error.errors[0]?.message };
    } catch {
      return { valid: false, error: "Invalid UUID format" };
    }
  },
  
  /**
   * Validate pagination parameters
   */
  validatePagination: (params: unknown): { 
    valid: boolean; 
    data?: { page: number; limit: number; offset?: number }; 
    error?: string 
  } => {
    try {
      const result = ObjectValidation.pagination.safeParse(params);
      if (result.success) {
        return { valid: true, data: result.data };
      }
      return { valid: false, error: result.error.errors[0]?.message };
    } catch {
      return { valid: false, error: "Invalid pagination parameters" };
    }
  },
};

/**
 * Firebase-specific validation schemas
 */
export const FirebaseValidation = {
  // Firestore document ID
  documentId: z.string().min(1).max(1500).regex(/^[^/]+$/, "Document ID cannot contain slashes"),
  
  // Firestore collection path
  collectionPath: z.string().min(1).regex(/^[a-zA-Z0-9_-]+$/, "Invalid collection path"),
  
  // Storage path
  storagePath: z.string().min(1).max(1024).regex(/^[a-zA-Z0-9/_.-]+$/, "Invalid storage path"),
  
  // Storage bucket name
  bucketName: z.string().min(1).regex(/^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/, "Invalid bucket name"),
  
  // Firebase Auth UID
  authUid: z.string().min(1).max(128),
};

/**
 * Security validation schemas
 */
export const SecurityValidation = {
  // API key format
  apiKey: z.string().regex(/^[a-zA-Z0-9_-]{20,}$/, "Invalid API key format"),
  
  // Bearer token
  bearerToken: z.string().regex(/^Bearer\s+[A-Za-z0-9-._~+/]+=*$/, "Invalid bearer token format"),
  
  // JWT token
  jwtToken: z.string().regex(/^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]*$/, "Invalid JWT format"),
  
  // Password strength (min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special)
  strongPassword: z.string()
    .min(8, "Password must be at least 8 characters")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/[a-z]/, "Password must contain at least one lowercase letter")
    .regex(/[0-9]/, "Password must contain at least one number")
    .regex(/[^A-Za-z0-9]/, "Password must contain at least one special character"),
  
  // IP address (v4)
  ipv4: z.string().regex(
    /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
    "Invalid IPv4 address"
  ),
  
  // IP address (v6)
  ipv6: z.string().regex(
    /^(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))$/,
    "Invalid IPv6 address"
  ),
  
  // CORS origin
  corsOrigin: z.string().regex(
    /^(https?:\/\/)?([\w.-]+)(:\d{1,5})?$/,
    "Invalid CORS origin"
  ),
};

/**
 * Content validation schemas
 */
export const ContentValidation = {
  // File upload
  fileUpload: z.object({
    fileName: z.string().min(1).max(255).regex(/^[a-zA-Z0-9._-]+$/, "Invalid file name"),
    contentType: z.string().regex(/^[a-z]+\/[a-z0-9.+-]+$/, "Invalid content type"),
    size: z.number().positive().max(100 * 1024 * 1024), // Max 100MB
  }),
  
  // Image file types
  imageContentType: z.enum([
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/webp",
    "image/svg+xml",
  ]),
  
  // Document file types
  documentContentType: z.enum([
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "text/plain",
    "text/csv",
  ]),
  
  // Video file types
  videoContentType: z.enum([
    "video/mp4",
    "video/mpeg",
    "video/webm",
    "video/quicktime",
  ]),
};

/**
 * Create a validated API handler wrapper
 */
export function createValidatedHandler<TInput, TOutput>(
  schema: z.ZodSchema<TInput>,
  handler: (input: TInput) => Promise<TOutput> | TOutput
) {
  return async (input: unknown): Promise<TOutput> => {
    const result = schema.safeParse(input);
    if (!result.success) {
      const errors = result.error.errors.map((err: z.ZodIssue) => ({
        path: err.path.join('.'),
        message: err.message,
      }));
      throw new Error(`Validation failed: ${JSON.stringify(errors)}`);
    }
    return handler(result.data);
  };
}

/**
 * Validate request body middleware helper
 */
export function validateRequestBody<T>(schema: z.ZodSchema<T>) {
  return (body: unknown): T => {
    const result = schema.safeParse(body);
    if (!result.success) {
      const errors = result.error.errors.map((err: z.ZodIssue) => ({
        path: err.path.join('.'),
        message: err.message,
      }));
      throw new Error(`Invalid request body: ${JSON.stringify(errors)}`);
    }
    return result.data;
  };
}
